import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Box, Card, Typography, TextField, Button, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { API_BASE_URL } from '@/config/api.enpoints';
import { useDashboardTheme } from '@/pages/MeetingDashboard/themeHelper';
import DropdownMain from '@/components/Inputs/DropdownMain';

const TABS = ['call', 'demo', 'negotiation', 'objection'];

const AVATARS = [
  { id: 'f1', name: 'Sarah (Female)', image: '/assets/twins/female1.png' },
  { id: 'f2', name: 'Emily (Female)', image: '/assets/twins/female2.png' },
  { id: 'm1', name: 'James (Male)', image: '/assets/twins/male1.png' },
  { id: 'm2', name: 'Robert (Male)', image: '/assets/twins/male2.png' },
];

const buyerPersonaOptions = [
  { label: 'C-Level', value: 'C_LEVEL' },
  { label: 'VP', value: 'VP' },
  { label: 'AVP', value: 'AVP' },
  { label: 'SVP', value: 'SVP' },
  { label: 'Director', value: 'DIRECTOR' },
  { label: 'Owner', value: 'OWNER' },
  { label: 'Consumer', value: 'CONSUMER' },
  { label: 'Board of Directors', value: 'BOARD' },
];

const businessModelOptions = [
  { label: 'B2B', value: 'B2B' },
  { label: 'B2C', value: 'B2C' },
  { label: 'B2B2C', value: 'B2B2C' },
];

const decisionPowerOptions = [
  { label: 'Decision Maker', value: 'DECISION_MAKER' },
  { label: 'Influencer', value: 'INFLUENCER' },
  { label: 'Champion', value: 'CHAMPION' },
  { label: 'Blocker', value: 'BLOCKER' },
];

const communicationStyleOptions = [
  { label: 'Direct', value: 'DIRECT' },
  { label: 'Indirect', value: 'INDIRECT' },
  { label: 'Detail-Oriented', value: 'DETAIL_ORIENTED' },
  { label: 'High-Level', value: 'HIGH_LEVEL' },
  { label: 'Fast-Paced', value: 'FAST_PACED' },
  { label: 'Methodical', value: 'METHODICAL' },
];

const relationshipOptions = [
  { label: 'Cold', value: 'COLD' },
  { label: 'Warm Intro', value: 'WARM_INTRO' },
  { label: 'Existing Relationship', value: 'EXISTING_RELATIONSHIP' },
];

const dealMaturityOptions = [
  { label: 'Early Stage', value: 'EARLY' },
  { label: 'Mid Stage', value: 'MID' },
  { label: 'Late Stage', value: 'LATE' },
];

const voiceOptions = [
  { label: 'Aura Asteria (Female)', value: 'aura-asteria-en' },
  { label: 'Aura Luna (Female)', value: 'aura-luna-en' },
  { label: 'Aura Orion (Male)', value: 'aura-orion-en' },
  { label: 'Aura Arcas (Male)', value: 'aura-arcas-en' },
];

const tonalityOptions = [
  { label: 'Professional', value: 'Professional' },
  { label: 'Casual', value: 'Casual' },
  { label: 'Assertive', value: 'Assertive' },
  { label: 'Empathetic', value: 'Empathetic' },
];

const functionalityOptions = [
  { label: 'Objection Handling', value: 'Objection Handling' },
  { label: 'Discovery Call', value: 'Discovery Call' },
  { label: 'Closing Pitch', value: 'Closing Pitch' },
];

export default function DigitalTwinTemplatesAdmin() {
  const { isDark, colors } = useDashboardTheme();
  const accent = isDark ? "#B8F890" : "#5a9d50";
  const [activeTab, setActiveTab] = useState('call');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemTemplates, setSystemTemplates] = useState<any>(null);

  const [formData, setFormData] = useState<any>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/v1/digital-twin/templates`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setSystemTemplates(result.data);
      }
    } catch (err) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    setValidationErrors({});
    if (systemTemplates) {
      const company = systemTemplates.companies?.find((c: any) => c.digitalTwinTemplate === activeTab) || {};
      const contact = systemTemplates.contacts?.find((c: any) => c.digitalTwinTemplate === activeTab) || {};
      
      setFormData({
        // TargetCompanyProfile
        companyName: company.companyName || '',
        industry: company.industry || '',
        businessModel: company.businessModel || '',
        decisionProcess: company.decisionProcess || '',
        competitorsInvolved: company.competitorsInvolved || '',
        techStack: company.techStack || '',
        currentVendors: company.currentVendors || '',
        commonObjections: company.commonObjections || '',
        companyIntelligence: company.companyIntelligence || '',
        howTheyMakeMoney: company.howTheyMakeMoney || '',
        notes: company.notes || '',
        painPoints: company.painPoints || '',
        pricingSignals: company.pricingSignals || '',
        productsServices: company.productsServices || '',
        targetCustomers: company.targetCustomers || '',

        // Company_Contacts
        contactFirstName: contact.firstName || '',
        contactLastName: contact.lastName || '',
        department: contact.department || '',
        jobTitle: contact.jobTitle || '',
        email: contact.email || '',
        phoneNumber: contact.phoneNumber || '',
        buyerPersona: Array.isArray(contact.buyerPersona) 
          ? (contact.buyerPersona[0] || '') 
          : (contact.buyerPersona ? contact.buyerPersona.split(',')[0]?.trim() || '' : ''),
        buyerNotes: contact.buyerNotes || '',
        buyerPainPoints: contact.buyerPainPoints || '',
        contactCommonObjections: contact.commonObjections || '',
        communicationStyle: contact.communicationStyle || '',
        dealIntelligence: contact.dealIntelligence || '',
        dealMaturity: contact.dealMaturity || 'EARLY',
        decisionPower: contact.decisionPower || '',
        primaryGoals: contact.primaryGoals || '',
        relationshipToRep: contact.relationshipToRep || '',
        solutionFocus: contact.solutionFocus || '',
        
        // Twin Config
        twinId: contact.twinId || 'f1',
        defaultVoice: contact.defaultVoice || 'aura-asteria-en',
        tonality: contact.tonality || '',
        functionality: contact.functionality || '',
      });
    }
  }, [activeTab, systemTemplates]);

  const [fetchingAI, setFetchingAI] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [lastFetchedUrl, setLastFetchedUrl] = useState('');

  const fetchCompanyDetails = async () => {
    if (!websiteUrl) {
      toast.error('Please enter a website URL first.');
      return;
    }
    const trimmedUrl = websiteUrl.trim().toLowerCase();
    if (lastFetchedUrl && lastFetchedUrl === trimmedUrl) {
      toast.error('Details for this URL have already been fetched.');
      return;
    }
    setFetchingAI(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/customer/generate-from-url`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url: websiteUrl })
      });
      const result = await response.json();
      if (response.ok && result.data) {
        setLastFetchedUrl(trimmedUrl);
        toast.success("Details fetched! Please confirm the details before saving.");
        const data = result.data;
        setFormData((prev: any) => ({
          ...prev,
          companyName: data.companyName || prev.companyName,
          industry: data.industry || prev.industry,
          businessModel: Array.isArray(data.businessModel)
            ? (data.businessModel[0] || prev.businessModel)
            : (data.businessModel || prev.businessModel),
          decisionProcess: data.makingProcess || data.decisionProcess || prev.decisionProcess,
          competitorsInvolved: data.competitorsInvolved || prev.competitorsInvolved,
          techStack: data.techStack || prev.techStack,
          currentVendors: data.currentVendors || prev.currentVendors,
          commonObjections: data.commonObjections || prev.commonObjections,
          companyIntelligence: data.companyIntelligence || prev.companyIntelligence,
          howTheyMakeMoney: data.howTheyMakeMoney || prev.howTheyMakeMoney,
          painPoints: data.painPoints || prev.painPoints,
          pricingSignals: data.pricingSignals || prev.pricingSignals,
          productsServices: data.productsServices || prev.productsServices,
          targetCustomers: data.targetCustomers || prev.targetCustomers,
        }));
      } else {
        toast.error(result.message || "Could not fetch details due to site configuration. Please fill manually.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Error: Try again in 1 min, or fill manually.");
    } finally {
      setFetchingAI(false);
    }
  };

  const fillTestData = () => {
    setFormData((prev: any) => ({
      ...prev,
      companyName: "Acme Corp",
      industry: "Software & Technology",
      businessModel: "B2B",
      decisionProcess: "Committee review",
      competitorsInvolved: "Salesforce, Hubspot",
      techStack: "React, Node, AWS",
      currentVendors: "Google Cloud",
      commonObjections: "Too expensive, Implementation takes too long",
      companyIntelligence: "Just raised $50M Series B.",
      howTheyMakeMoney: "Subscription revenue",
      notes: "High priority target",
      painPoints: "Siloed data, manual reporting",
      pricingSignals: "Budget unlocks in Q3",
      productsServices: "CRM Platform",
      targetCustomers: "Mid-market to Enterprise",

      contactFirstName: "Sarah",
      contactLastName: "Connor",
      department: "Sales",
      jobTitle: "VP of Sales",
      email: "sarah@acme.com",
      phoneNumber: "555-0199",
      buyerPersona: "VP",
      buyerNotes: "Prefers concise emails",
      buyerPainPoints: "Team missing quotas",
      contactCommonObjections: "No budget right now",
      communicationStyle: "Direct and results-oriented",
      dealIntelligence: "Looking to consolidate tools",
      dealMaturity: "MID",
      decisionPower: "High",
      primaryGoals: "Increase win rate by 15%",
      relationshipToRep: "Warm",
      solutionFocus: "Analytics and Automation"
    }));
    toast.success("Test data filled!");
  };

  const handleChange = (field: string) => (e: any) => {
    const val = e.target.value;
    setFormData((prev: any) => ({ ...prev, [field]: val }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSave = async () => {
    const errors: Record<string, boolean> = {};

    if (!formData.companyName?.trim()) {
      errors.companyName = true;
    }
    if (!formData.industry?.trim()) {
      errors.industry = true;
    }
    if (!formData.businessModel?.trim()) {
      errors.businessModel = true;
    }
    if (!formData.contactFirstName?.trim()) {
      errors.contactFirstName = true;
    }
    if (!formData.contactLastName?.trim()) {
      errors.contactLastName = true;
    }
    const buyerPersonaVal = Array.isArray(formData.buyerPersona) ? formData.buyerPersona[0] : formData.buyerPersona;
    if (!buyerPersonaVal || !buyerPersonaVal.trim()) {
      errors.buyerPersona = true;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error("Please fill all mandatory fields (marked with *).");
      return;
    }

    setValidationErrors({});

    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_URL}/v1/digital-twin/templates/${activeTab}`, {
        method: 'PUT',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (res.ok && result.success) {
        toast.success("Template updated successfully");
        fetchTemplates(); // Refresh
      } else {
        toast.error(result.error || "Failed to save template");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };


  const accordionStyle = { backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '16px !important', mb: 2, '&:before': { display: 'none' } };

  return (
    <Box className="p-4 md:p-8 font-poppins w-full" style={{ background: colors.bg, color: colors.text }}>
      <Box className="w-full">
        <Typography variant="h4" className="font-black tracking-tight mb-8" style={{ color: colors.text }}>
          Manage Default Templates
        </Typography>

        <div className="flex space-x-2 sm:space-x-4 mb-6">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-6 py-2 rounded-full font-bold transition-all cursor-pointer"
              style={{
                background: activeTab === tab ? (isDark ? "#9333ea" : "#5a9d50") : colors.surface,
                color: activeTab === tab ? "#ffffff" : colors.text,
                border: `1px solid ${activeTab === tab ? (isDark ? "#9333ea" : "#5a9d50") : colors.border}`,
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {import.meta.env.DEV && (
          <div className="mb-8">
            <Button
              variant="outlined"
              onClick={fillTestData}
              sx={{ color: isDark ? '#4ade80' : '#15803D', borderColor: isDark ? 'rgba(74,222,128,0.5)' : 'rgba(21,128,61,0.5)', '&:hover': { borderColor: isDark ? '#4ade80' : '#15803D', background: isDark ? 'rgba(74,222,128,0.1)' : 'rgba(21,128,61,0.1)' }, borderRadius: '8px', textTransform: 'none' }}
            >
              🚀 Fill Test Data (Local Dev Only)
            </Button>
          </div>
        )}

        {loading ? (
          <Box className="flex justify-center py-20">
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <div className="space-y-4">
            
            {/* Visual Avatar Selection */}
            <Card sx={{ backgroundColor: "var(--color-surface)" }} className="p-6 md:p-8 rounded-2xl border border-[var(--color-border)] shadow-xl mb-6">
              <Typography variant="h6" sx={{ color: "var(--color-text)", borderColor: "var(--color-border)" }} className="font-bold mb-6 border-b pb-3">
                Select Twin Avatar
              </Typography>
              <div className="flex flex-wrap gap-5 items-center">
                {AVATARS.map((avatar) => (
                  <Box 
                    key={avatar.id}
                    onClick={() => {
                      const voiceMap: Record<string, string> = {
                        f1: 'aura-asteria-en',
                        f2: 'aura-luna-en',
                        m1: 'aura-orion-en',
                        m2: 'aura-arcas-en',
                      };
                      setFormData((prev: any) => ({
                        ...prev,
                        twinId: avatar.id,
                        defaultVoice: voiceMap[avatar.id] || 'aura-asteria-en',
                        tonality: prev.tonality || 'Professional',
                        functionality: prev.functionality || 'Objection Handling',
                      }));
                    }}
                    className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-300 ease-out hover:scale-105 w-[150px] sm:w-[170px] ${
                      formData.twinId === avatar.id ? 'border-purple-500 shadow-[0_0_15px_rgba(139,92,246,0.4)]' : 'border-transparent hover:border-black/20'
                    }`}
                  >
                    <div className="h-[180px] w-full bg-white/5 relative">
                      <img src={avatar.image} alt={avatar.name} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md p-1.5">
                        <Typography className="text-center text-xs font-semibold text-white truncate">{avatar.name}</Typography>
                      </div>
                    </div>
                  </Box>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                  <div className="col-span-1">
                    <DropdownMain
                      label="Default Voice"
                      value={formData.defaultVoice || 'aura-asteria-en'}
                      onChange={handleChange('defaultVoice')}
                      options={voiceOptions}
                      showClearOption={false}
                    />
                  </div>
                  <div className="col-span-1">
                    <DropdownMain
                      label="Tonality"
                      value={formData.tonality || ''}
                      onChange={handleChange('tonality')}
                      options={tonalityOptions}
                      showClearOption={false}
                    />
                  </div>
                  <div className="col-span-1">
                    <DropdownMain
                      label="Functionality"
                      value={formData.functionality || ''}
                      onChange={handleChange('functionality')}
                      options={functionalityOptions}
                      showClearOption={false}
                    />
                  </div>
              </div>
            </Card>

            <Accordion sx={accordionStyle} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text)' }} />}>
                <Typography variant="h6" className="font-bold">Target Company Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div className="flex flex-col md:flex-row gap-4 mb-6 p-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-xl items-center">
                   <TextField 
                     fullWidth 
                     label="Company Website URL" 
                     placeholder="https://example.com"
                     variant="outlined" 
                     size="small" 
                     value={websiteUrl} 
                     onChange={(e) => setWebsiteUrl(e.target.value)} 
                     InputLabelProps={{ style: { color: 'var(--color-muted)' } }} 
                     sx={{ '& .MuiInputBase-input': { color: 'var(--color-text)' }, '& .MuiOutlinedInput-root': { backgroundColor: 'var(--color-surface)', borderRadius: '12px', border: '1px solid var(--color-border)' } }} 
                   />
                   <Button
                    variant="contained"
                    onClick={fetchCompanyDetails}
                    disabled={fetchingAI}
                    sx={{
                      bgcolor: accent,
                      color: isDark ? "#000000" : "#ffffff",
                      "&:hover": { bgcolor: isDark ? "#a0e070" : "#4c8a43" },
                      "&:disabled": { bgcolor: isDark ? "rgba(184, 248, 144, 0.4)" : "rgba(90, 157, 80, 0.4)", color: "rgba(0,0,0,0.5)" },
                      fontWeight: "bold",
                      borderRadius: "12px",
                      px: 3,
                      py: 1.2,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      boxShadow: `0 0 15px ${accent}40`,
                    }}
                   >
                     {fetchingAI ? "Scraping..." : "Fetch Details (AI)"}
                   </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Company Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="e.g. Acme Corp" value={formData.companyName || ''} onChange={handleChange('companyName')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: validationErrors.companyName ? '2px solid #ef4444' : (isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1'), minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Industry <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="e.g. SaaS, FinTech" value={formData.industry || ''} onChange={handleChange('industry')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: validationErrors.industry ? '2px solid #ef4444' : (isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1'), minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <DropdownMain
                      label="Business Model"
                      required={true}
                      value={formData.businessModel || ''}
                      onChange={handleChange('businessModel')}
                      options={businessModelOptions}
                      error={validationErrors.businessModel}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Decision Process
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="How decisions get made" value={formData.decisionProcess || ''} onChange={handleChange('decisionProcess')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Competitors Involved
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Who else are they evaluating?" value={formData.competitorsInvolved || ''} onChange={handleChange('competitorsInvolved')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Tech Stack
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Tools & platforms they use" value={formData.techStack || ''} onChange={handleChange('techStack')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Current Vendors
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Who they currently use" value={formData.currentVendors || ''} onChange={handleChange('currentVendors')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Common Objections
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Typical pushbacks" value={formData.commonObjections || ''} onChange={handleChange('commonObjections')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Company Intelligence
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Recent news, triggers, context" value={formData.companyIntelligence || ''} onChange={handleChange('companyIntelligence')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      How They Make Money
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Revenue model" value={formData.howTheyMakeMoney || ''} onChange={handleChange('howTheyMakeMoney')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Notes
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Anything else about this deal" value={formData.notes || ''} onChange={handleChange('notes')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Pain Points
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Main challenges they face" value={formData.painPoints || ''} onChange={handleChange('painPoints')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Pricing Signals
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Budget range, pricing sensitivity" value={formData.pricingSignals || ''} onChange={handleChange('pricingSignals')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Products/Services
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Main offerings" value={formData.productsServices || ''} onChange={handleChange('productsServices')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Target Customers
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Who they sell to" value={formData.targetCustomers || ''} onChange={handleChange('targetCustomers')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={accordionStyle} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text)' }} />}>
                <Typography variant="h6" className="font-bold">Contact Person (Buyer Persona)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      First Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Enter first name" value={formData.contactFirstName || ''} onChange={handleChange('contactFirstName')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: validationErrors.contactFirstName ? '2px solid #ef4444' : (isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1'), minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Last Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Enter last name" value={formData.contactLastName || ''} onChange={handleChange('contactLastName')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: validationErrors.contactLastName ? '2px solid #ef4444' : (isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1'), minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <DropdownMain
                      label="Buyer Personas"
                      required={true}
                      value={Array.isArray(formData.buyerPersona) ? (formData.buyerPersona[0] || '') : (formData.buyerPersona || '')}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev: any) => ({ ...prev, buyerPersona: val }));
                        if (validationErrors.buyerPersona && val) {
                          setValidationErrors((prev) => {
                            const next = { ...prev };
                            delete next.buyerPersona;
                            return next;
                          });
                        }
                      }}
                      options={buyerPersonaOptions}
                      error={validationErrors.buyerPersona}
                    />
                  </div>

                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Email
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Enter email address" value={formData.email || ''} onChange={handleChange('email')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Phone Number
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Enter phone number" value={formData.phoneNumber || ''} onChange={handleChange('phoneNumber')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Department
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Enter department" value={formData.department || ''} onChange={handleChange('department')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Job Title
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Enter job title" value={formData.jobTitle || ''} onChange={handleChange('jobTitle')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Buyer Notes
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Add anything you know about this buyer..." value={formData.buyerNotes || ''} onChange={handleChange('buyerNotes')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Buyer Pain Points
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Describe buyer pain points" value={formData.buyerPainPoints || ''} onChange={handleChange('buyerPainPoints')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Common Objections
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Describe common objections" value={formData.contactCommonObjections || ''} onChange={handleChange('contactCommonObjections')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <DropdownMain
                      label="Communication Style"
                      value={formData.communicationStyle || ''}
                      onChange={handleChange('communicationStyle')}
                      options={communicationStyleOptions}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Deal Intelligence
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Summarize deal intelligence" value={formData.dealIntelligence || ''} onChange={handleChange('dealIntelligence')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  
                  <div className="col-span-1">
                    <DropdownMain
                      label="Deal Maturity"
                      value={formData.dealMaturity || 'EARLY'}
                      onChange={handleChange('dealMaturity')}
                      options={dealMaturityOptions}
                      showClearOption={false}
                    />
                  </div>

                  <div className="col-span-1">
                    <DropdownMain
                      label="Decision Power"
                      value={formData.decisionPower || ''}
                      onChange={handleChange('decisionPower')}
                      options={decisionPowerOptions}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Primary Goals
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Describe primary goals or KPIs" value={formData.primaryGoals || ''} onChange={handleChange('primaryGoals')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                  <div className="col-span-1">
                    <DropdownMain
                      label="Relationship to Rep"
                      value={formData.relationshipToRep || ''}
                      onChange={handleChange('relationshipToRep')}
                      options={relationshipOptions}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className={`block text-sm my-1 pt-2 ${isDark ? 'text-white' : 'text-purple-950 font-bold'}`}>
                      Solution Focus
                    </label>
                    <TextField fullWidth variant="outlined" size="small" placeholder="Analytics and Automation" value={formData.solutionFocus || ''} onChange={handleChange('solutionFocus')} sx={{ marginTop: '8px', '& .MuiInputBase-input': { color: isDark ? 'white' : '#0f172a', padding: '10px 14px' }, '& .MuiOutlinedInput-root': { backgroundColor: isDark ? 'var(--color-surface)' : '#ffffff', borderRadius: '12px', border: isDark ? '1px solid var(--color-border)' : '1px solid #cbd5e1', minHeight: '44px', '& fieldset': { border: 'none' } } }} />
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>

            <div className="flex justify-end mt-8 border-[#313131] pt-6 mb-12">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  bgcolor: accent,
                  color: isDark ? "#000000" : "#ffffff",
                  "&:hover": { bgcolor: isDark ? "#a0e070" : "#4c8a43" },
                  "&:disabled": { bgcolor: isDark ? "rgba(184, 248, 144, 0.4)" : "rgba(90, 157, 80, 0.4)", color: "rgba(0,0,0,0.5)" },
                  fontWeight: "bold",
                  borderRadius: "14px",
                  px: 6,
                  py: 1.5,
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: `0 0 20px ${accent}4d`,
                  whiteSpace: "nowrap",
                }}
              >
                {saving ? "Saving..." : "Save Template"}
              </Button>
            </div>
          </div>
        )}
      </Box>
    </Box>
  );
}
